import { useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { formatDate, getMonthToDateRange } from '@/utils/date';
import { getJobCardColumns } from '../components/JobCardColumns';
import { useJobCard } from './useJobCard';
import type { JobCardQueryOptionsFn } from '../api/attendance.api';

export type JobCardRoute = '/attendance/job-card' | '/attendance/shift-job-card';

interface UseJobCardPageParams {
  queryOptions: JobCardQueryOptionsFn;
  employeeReportRoute: string;
  selfRoute: JobCardRoute;
}

interface JobCardSearch {
  dateFrom?: string;
  dateTo?: string;
  isActive?: boolean;
  q?: string;
}

export function useJobCardPage({
  queryOptions,
  employeeReportRoute,
  selfRoute,
}: UseJobCardPageParams) {
  const search: JobCardSearch = useSearch({ strict: false });
  const navigate = useNavigate();

  const defaults = getMonthToDateRange();
  const today = formatDate(new Date());

  const dateFrom = search.dateFrom ?? defaults.dateFrom;
  const dateTo = search.dateTo ?? defaults.dateTo;
  const isActive = search.isActive ?? true;
  const searchText = search.q ?? '';

  function patch(next: JobCardSearch) {
    void navigate({
      to: selfRoute,
      search: { dateFrom, dateTo, isActive, q: searchText, ...next },
      replace: true,
    });
  }

  function setDateFrom(next: string) {
    patch(next > dateTo ? { dateFrom: next, dateTo: next } : { dateFrom: next });
  }

  function setDateTo(next: string) {
    patch(next < dateFrom ? { dateFrom: next, dateTo: next } : { dateTo: next });
  }

  function setIsActive(next: boolean) {
    patch({ isActive: next });
  }

  function setSearchText(next: string) {
    patch({ q: next });
  }

  const { filtered, summary, isLoading, error, refetch } = useJobCard({
    queryOptions,
    dateFrom,
    dateTo,
    search: searchText,
    isActive,
  });

  const columns = useMemo(
    () =>
      getJobCardColumns({
        searchText: searchText || undefined,
        onViewReport: (item) => {
          if (item.zmUserId == null) return;
          void navigate({
            to: employeeReportRoute,
            search: { userId: item.zmUserId, dateFrom, dateTo },
          });
        },
      }),
    [navigate, employeeReportRoute, dateFrom, dateTo, searchText],
  );

  return {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    today,
    isActive,
    setIsActive,
    searchText,
    setSearchText,
    filtered,
    summary,
    columns,
    isLoading,
    error,
    refetch,
  };
}
